/** @import { Serve } from 'bun' */
import fs from 'node:fs';
import process from 'node:process';
import { server_options } from '#@sveltejs/adapter-bun';
import { routes } from './routes.js';
import { handler } from './handler.js';
import { boolean_env, bytes_env, env, number_env } from './env.js';

// nothing enforces `engines.bun` at install time, and the build may have run on a newer Bun.
// order() rather than satisfies(): a range would reject canary builds such as 1.5.0-canary.1
if (Bun.semver.order(Bun.version, '1.4.0') < 0) {
	throw new Error(
		`@sveltejs/adapter-bun requires Bun 1.4 or newer, but this is Bun ${Bun.version}`
	);
}

const options = /** @type {Serve.Options<undefined>} */ ({ ...server_options });

const unix = env('SOCKET_PATH', options.unix);

if (unix) {
	options.unix = unix;
	delete options.hostname;
	delete options.port;
	delete options.reusePort;
	delete options.ipv6Only;

	// an unclean shutdown leaves the socket file behind and the next listen would
	// fail with EADDRINUSE; the zero-size check (same heuristic as adapter-node)
	// avoids deleting a regular file that happens to sit at this path
	try {
		if (fs.statSync(unix).size === 0) fs.rmSync(unix);
	} catch {
		// ignore
	}
} else {
	delete options.unix;
	options.hostname = env('HOST', options.hostname);
	// always set an explicit port: left undefined, Bun.serve reads the unprefixed
	// BUN_PORT/PORT/NODE_PORT itself, bypassing envPrefix isolation
	options.port = env('PORT', options.port?.toString()) ?? 3000;
	options.reusePort = boolean_env('REUSE_PORT', options.reusePort);
	options.ipv6Only = boolean_env('IPV6_ONLY', options.ipv6Only);
}

// not IDLE_TIMEOUT: that name means idle-shutdown seconds on adapter-node, and a
// carried-over value would crash on the 255 cap or silently kill slow requests
options.idleTimeout = number_env('CONNECTION_IDLE_TIMEOUT', options.idleTimeout, { max: 255 });
options.development = boolean_env('DEVELOPMENT') ?? options.development ?? false;

options.maxRequestBodySize = bytes_env('BODY_SIZE_LIMIT', options.maxRequestBodySize ?? 512 * 1024);

const shutdown_timeout = number_env('SHUTDOWN_TIMEOUT', 30);

options.fetch = handler;
options.routes = routes;

const server = Bun.serve(options);

console.log(unix ? `Listening on ${unix}` : `Listening on ${server.url}`);

let shutting_down = false;

/** @param {'SIGINT' | 'SIGTERM'} reason */
async function graceful_shutdown(reason) {
	if (shutting_down) return process.exit(1);
	shutting_down = true;

	if (server.pendingRequests !== 0) {
		console.log(
			`Waiting for ${server.pendingRequests} requests to finish before shutting down...\n` +
				'Press Ctrl+C again to force shutdown.'
		);
	}

	// stop() waits for in-flight requests, and an open event stream is one forever,
	// so race the drain against the deadline. The timer must stay referenced: a
	// draining server no longer holds the event loop open on its own
	/** @type {ReturnType<typeof setTimeout> | undefined} */
	let deadline;
	const drained = await Promise.race([
		server.stop().then(() => true),
		new Promise((resolve) => {
			deadline = setTimeout(() => resolve(false), shutdown_timeout * 1000);
		})
	]);
	clearTimeout(deadline);

	// force-close aborts the in-flight handlers, so shutdown listeners do not tear
	// down resources those handlers still hold
	if (!drained) await server.stop(true);

	// @ts-expect-error custom events cannot be typed
	process.emit('sveltekit:shutdown', reason);
}

process.on('SIGTERM', () => graceful_shutdown('SIGTERM'));
process.on('SIGINT', () => graceful_shutdown('SIGINT'));
